<template>
  <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
    <!-- 左侧：总数和每页数量选择 -->
    <div class="flex items-center gap-4">
      <div class="text-sm text-gray-700 dark:text-gray-300">
        共 {{ total }} 条记录
      </div>
      <div v-if="showPageSizeSelector" class="flex items-center gap-2">
        <label class="text-sm text-gray-700 dark:text-gray-300">每页显示：</label>
        <select
          :value="pageSize"
          @change="handlePageSizeChange"
          class="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-[#8ba2ec] dark:focus:ring-[#6b82cc] focus:border-transparent"
        >
          <option v-for="size in pageSizeOptions" :key="size" :value="size">
            {{ size }} 条
          </option>
        </select>
      </div>
    </div>

    <!-- 右侧：分页控制 -->
    <div class="flex items-center gap-2">
      <!-- 首页 -->
      <button
        @click="handlePageChange(1)"
        :disabled="currentPage === 1"
        class="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        首页
      </button>

      <!-- 上一页 -->
      <button
        @click="handlePageChange(currentPage - 1)"
        :disabled="currentPage === 1"
        class="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        上一页
      </button>

      <!-- 页码显示 -->
      <div class="flex items-center gap-1">
        <template v-for="page in visiblePages" :key="page">
          <button
            v-if="typeof page === 'number'"
            @click="handlePageChange(page)"
            :class="[
              'px-3 py-1 border rounded transition-colors',
              page === currentPage
                ? 'bg-gradient-to-br from-[#8ba2ec] to-[#9599e2] dark:from-[#6b82cc] dark:to-[#7579c2] text-white border-transparent hover:from-[#7f98e9] hover:to-[#8185df] dark:hover:from-[#5f78c9] dark:hover:to-[#6165bf]'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            ]"
          >
            {{ page }}
          </button>
          <span v-else class="px-2 dark:text-gray-400">...</span>
        </template>
      </div>

      <!-- 下一页 -->
      <button
        @click="handlePageChange(currentPage + 1)"
        :disabled="currentPage >= totalPages"
        class="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        下一页
      </button>

      <!-- 末页 -->
      <button
        @click="handlePageChange(totalPages)"
        :disabled="currentPage >= totalPages"
        class="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        末页
      </button>

      <!-- 跳转 -->
      <div v-if="showJumper" class="flex items-center gap-2 ml-2">
        <span class="text-sm text-gray-700 dark:text-gray-300">跳转到</span>
        <input
          v-model.number="jumpPage"
          type="number"
          min="1"
          :max="totalPages"
          class="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-center bg-white dark:bg-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-[#8ba2ec] dark:focus:ring-[#6b82cc] focus:border-transparent"
          @keyup.enter="handleJump"
        />
        <button
          @click="handleJump"
          class="px-3 py-1 bg-gradient-to-br from-[#8ba2ec] to-[#9599e2] dark:from-[#6b82cc] dark:to-[#7579c2] text-white rounded text-sm hover:from-[#7f98e9] hover:to-[#8185df] dark:hover:from-[#5f78c9] dark:hover:to-[#6165bf] transition-colors"
        >
          跳转
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue';

/**
 * Props 定义
 */
interface Props {
  total: number;
  currentPage: number;
  pageSize: number;
  showPageSizeSelector?: boolean;
  showJumper?: boolean;
  pageSizeOptions?: number[];
}

const props = withDefaults(defineProps<Props>(), {
  showPageSizeSelector: true,
  showJumper: true,
  pageSizeOptions: () => [10, 20, 50, 100],
});

/**
 * Emits 定义
 */
const emit = defineEmits<{
  'update:currentPage': [page: number];
  'update:pageSize': [size: number];
}>();

/**
 * 响应式数据
 */
const jumpPage = ref<number>(props.currentPage);

/**
 * 同步跳转输入框与外部页码
 */
watch(() => props.currentPage, (val) => {
  jumpPage.value = val;
});

/**
 * 总页数
 */
const totalPages = computed(() => Math.max(1, Math.ceil(props.total / props.pageSize)));

/**
 * 可见的页码列表
 */
const visiblePages = computed(() => {
  const pages: (number | string)[] = [];
  const maxVisible = 7;

  if (totalPages.value <= maxVisible) {
    for (let i = 1; i <= totalPages.value; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);

    if (props.currentPage > 3) {
      pages.push('...');
    }

    const start = Math.max(2, props.currentPage - 1);
    const end = Math.min(totalPages.value - 1, props.currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (props.currentPage < totalPages.value - 2) {
      pages.push('...');
    }

    pages.push(totalPages.value);
  }

  return pages;
});

/**
 * 处理页码变化（含边界保护）
 */
const handlePageChange = (page: number) => {
  const clamped = Math.max(1, Math.min(page, totalPages.value));
  if (clamped !== props.currentPage) {
    emit('update:currentPage', clamped);
  }
};

/**
 * 处理每页数量变化（重置到第 1 页）
 */
const handlePageSizeChange = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  emit('update:pageSize', Number(target.value));
  // 切换每页条数后回到第 1 页
  emit('update:currentPage', 1);
};

/**
 * 处理跳转（含边界保护）
 */
const handleJump = () => {
  const target = Math.max(1, Math.min(jumpPage.value || 1, totalPages.value));
  jumpPage.value = target;
  handlePageChange(target);
};
</script>
